import { GroupEntity } from '@internal/plugin-catalog-model';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  RELATION_CHILD_OF,
  RELATION_PARENT_OF,
  stringifyEntityRef,
  Entity,
} from '@backstage/catalog-model';
import {
  InfoCard,
  InfoCardVariants,
  Progress,
  Link,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';

import {
  Box,
  Typography,
  makeStyles,
  Collapse,
  IconButton,
  Divider,
  Grid,
  Button,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GroupIcon from '@material-ui/icons/Group';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PersonIcon from '@material-ui/icons/Person';
import Alert from '@material-ui/lab/Alert';

import { ConfluenceLogo } from '../../Badges/ConfluenceLogo';
import { JiraProjectsLogo } from '../../Badges/JiraLogo';
import { SlackLogo } from '../../Badges/SlackLogo';

const useStyles = makeStyles(theme => ({
  compactCard: {
    height: '100%',
    minHeight: 410,
    display: 'flex',
    flexDirection: 'column',
    '& .MuiCard-root': {
      height: '100% !important',
      minHeight: 'unset !important',
      display: 'flex',
      flexDirection: 'column',
    },
    '& .MuiCardContent-root': {
      padding: `${theme.spacing(2)}px !important`,
      minHeight: 'unset !important',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      '&:last-child': { paddingBottom: `${theme.spacing(2)}px !important` },
    },
    '& .MuiCardHeader-root': {
      padding: `${theme.spacing(2)}px !important`,
      paddingBottom: `${theme.spacing(1)}px !important`,
    },
    '& .MuiCardHeader-action': { margin: 0, alignSelf: 'center' },
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: 'unset',
    flex: 1,
    overflow: 'hidden',
  },
  mainContent: {
    display: 'flex',
    gap: theme.spacing(2),
    flex: 1,
    overflow: 'hidden',
  },
  leftSection: {
    flex: '0 0 65%',
    overflow: 'auto',
  },
  rightSection: {
    flex: '0 0 35%',
    borderLeft: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(2),
    overflow: 'auto',
  },
  sideListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  teamNode: {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:last-child': { borderBottom: 'none' },
    position: 'relative',
  },
  memberNode: {
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:last-child': { borderBottom: 'none' },
  },
  teamContent: { display: 'flex', alignItems: 'center', gap: theme.spacing(2) },
  teamIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  currentTeamIcon: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.error.main,
  },
  memberIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    flexShrink: 0,
  },
  teamDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    flex: 1,
  },
  teamName: { fontSize: 14, fontWeight: 600 },
  currentTeamName: { color: theme.palette.error.main },
  teamId: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
  },
  currentTeamId: {
    fontSize: 12,
    fontWeight: 500,
    fontFamily: 'monospace',
    color: theme.palette.error.main,
  },
  memberName: { fontSize: 13, fontWeight: 500 },
  memberEmail: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  parentTeamNode: { paddingLeft: theme.spacing(1) },
  currentTeamNode: { backgroundColor: theme.palette.grey[200] },
  parentLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: theme.spacing(0.5),
  },
  childrenLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingLeft: theme.spacing(2),
    borderLeft: `3px solid ${theme.palette.divider}`,
  },
  hierarchyContainer: {
    marginTop: theme.spacing(1),
    borderRadius: 0,
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
  hierarchyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: theme.spacing(1),
    borderRadius: 4,
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  hierarchyLine: {
    position: 'absolute',
    top: 36,
    left: 18,
    width: 2,
    backgroundColor: theme.palette.divider,
    bottom: -8,
    zIndex: 0,
  },
  sideListSection: {
    marginBottom: theme.spacing(1),
  },
  sideListHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    padding: '8px 0',
    '&:hover': {
      opacity: 0.8,
    },
  },
  sideListTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  sideListItems: {
    paddingLeft: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 4,
  },
  sideListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
  },
  sideListLink: {
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    textDecoration: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  membersSection: {
    marginTop: theme.spacing(1),
  },
  membersSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  membersGrid: {
    marginTop: theme.spacing(1),
  },
  memberGridItem: {
    padding: theme.spacing(0.5),
  },
  showMoreButton: {
    fontSize: 11,
    textTransform: 'none',
    marginTop: theme.spacing(1),
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
}));

const AzureAD: React.FC<{ entity: GroupEntity; isCurrent: boolean }> = ({
  entity,
  isCurrent = false,
}) => {
  const classes = useStyles();
  const azureName =
    entity.metadata.annotations?.['graph.microsoft.com/group-original-name'];
  if (!azureName) return null;

  return (
    <Typography className={isCurrent ? classes.currentTeamId : classes.teamId}>
      Azure AD Group: {azureName}
    </Typography>
  );
};

const ManagerNode: React.FC<{ managerName?: string; managerEmail: string }> = ({
  managerName,
  managerEmail,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const urlSafeEmail = managerEmail.replace(/@/g, '_');
  const onLinkClick = useCallback(() => {
    navigate(`/catalog/default/User/${urlSafeEmail}`);
    globalThis.location.reload();
  }, [urlSafeEmail, navigate]);

  return (
    <Link
      to={`/catalog/default/User/${urlSafeEmail}`}
      onClick={onLinkClick}
      underline="none"
      style={{ display: 'block' }}
    >
      <Box className={classes.teamNode}>
        <Box className={classes.teamContent}>
          <Box className={classes.teamIcon}>
            <PersonIcon style={{ fontSize: 20 }} />
          </Box>
          <Box className={classes.teamDetails}>
            {managerName && (
              <Typography className={classes.teamName}>
                {managerName.toUpperCase()}
              </Typography>
            )}
            <Typography className={classes.teamId}>{managerEmail}</Typography>
          </Box>
        </Box>
      </Box>
    </Link>
  );
};

const MemberNode: React.FC<{ member: Entity }> = ({ member }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const memberName =
    (member.spec?.profile as any)?.displayName ?? member.metadata.name;
  const memberEmail =
    (member.spec?.profile as any)?.email ?? member.metadata.name;
  const urlSafeEmail = memberEmail.replace(/@/g, '_');
  const onLinkClick = useCallback(() => {
    navigate(`/catalog/default/User/${urlSafeEmail}`);
    globalThis.location.reload();
  }, [urlSafeEmail, navigate]);

  return (
    <Link
      to={`/catalog/default/User/${urlSafeEmail}`}
      onClick={onLinkClick}
      underline="none"
      style={{ display: 'block' }}
    >
      <Box className={classes.memberNode}>
        <Box className={classes.teamContent}>
          <Box className={classes.memberIcon}>
            <PersonIcon style={{ fontSize: 18 }} />
          </Box>
          <Box className={classes.teamDetails}>
            <Typography className={classes.memberName}>{memberName}</Typography>
            <Typography className={classes.memberEmail}>
              {memberEmail}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Link>
  );
};

const TeamNode: React.FC<{
  entity: GroupEntity;
  isCurrent?: boolean;
  isParent?: boolean;
}> = ({ entity, isCurrent = false, isParent = false }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const displayName = entity.spec?.profile?.displayName ?? entity.metadata.name;
  const nodeClasses = `${classes.teamNode} ${
    isCurrent ? classes.currentTeamNode : ''
  } ${isParent ? classes.parentTeamNode : ''}`;
  const nameClasses = `${classes.teamName} ${
    isCurrent ? classes.currentTeamName : ''
  }`;
  const idClasses = isCurrent ? classes.currentTeamId : classes.teamId;
  const onLinkClick = useCallback(() => {
    navigate(`/catalog/default/group/${entity.metadata.name}`);
    globalThis.location.reload();
  }, [entity.metadata.name, navigate]);

  return (
    <Link
      key={entity.metadata.uid || entity.metadata.name}
      to={`/catalog/default/group/${entity.metadata.name}`}
      onClick={onLinkClick}
      underline="none"
      style={{ display: 'block', position: 'relative' }}
    >
      <Box className={nodeClasses}>
        <Box className={classes.teamContent}>
          <Box
            className={`${classes.teamIcon} ${
              isCurrent ? classes.currentTeamIcon : ''
            }`}
          >
            <GroupIcon style={{ fontSize: 20 }} />
          </Box>
          <Box className={classes.teamDetails}>
            <Typography className={nameClasses}>{displayName}</Typography>
            <Typography className={idClasses}>
              Supervisory Organization ID: {entity.metadata.name.toUpperCase()}
            </Typography>
            <AzureAD entity={entity} isCurrent={isCurrent} />
          </Box>
        </Box>
      </Box>
    </Link>
  );
};

interface SideListItem {
  label: string;
  value?: string;
  href?: string;
  icon?: React.ReactNode;
}

const SideListSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  count?: number;
  items: SideListItem[];
  defaultExpanded?: boolean;
}> = ({ icon, title, count, items, defaultExpanded = true }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  return (
    <Box className={classes.sideListSection}>
      <Box
        className={classes.sideListHeader}
        onClick={() => setExpanded(!expanded)}
      >
        {icon}
        <Typography className={classes.sideListTitle}>
          {title}
          {count !== undefined && <span> &bull; {count}</span>}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box className={classes.sideListItems}>
          {items.length > 0 ? (
            items.map(item => {
              const key = item.href ?? item.label;
              return (
                <Box key={key} className={classes.sideListItem}>
                  {item.icon}
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.sideListLink}
                    >
                      {item.label}
                      <OpenInNewIcon style={{ fontSize: 14 }} />
                    </a>
                  ) : (
                    <Typography style={{ fontSize: 13, color: '#888' }}>
                      {item.label}
                      {item.value && `: ${item.value}`}
                    </Typography>
                  )}
                  <Divider style={{ margin: '4px 0' }} />
                </Box>
              );
            })
          ) : (
            <Typography
              style={{ fontSize: 13, color: '#888', fontStyle: 'italic' }}
            >
              No projects
            </Typography>
          )}
        </Box>
      </Collapse>
      <Divider style={{ margin: '8px 0' }} />
    </Box>
  );
};

export const GroupProfileCard = (props: { variant?: InfoCardVariants }) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { entity: group } = useEntity<GroupEntity>();
  const [areChildrenExpanded, setAreChildrenExpanded] = React.useState(false);
  const [areMembersExpanded, setAreMembersExpanded] = React.useState(false);
  const [childEntities, setChildEntities] = React.useState<GroupEntity[]>([]);
  const [parentEntities, setParentEntities] = React.useState<GroupEntity[]>([]);
  const [memberEntities, setMemberEntities] = React.useState<Entity[]>([]);
  const [loadingChildren, setLoadingChildren] = React.useState(false);
  const [loadingParents, setLoadingParents] = React.useState(false);
  const [loadingMembers, setLoadingMembers] = React.useState(false);

  const jiraProjectKey = group.metadata.annotations?.['jira/project-key'];
  const jiraProjects = jiraProjectKey
    ? jiraProjectKey
        .split(',')
        .map(key => key.trim())
        .filter(Boolean)
    : [];
  const confluenceUrls = group.metadata.annotations?.['confluence/wiki-urls'];
  const confluenceLinks = confluenceUrls
    ? confluenceUrls
        .split(',')
        .map(url => url.trim())
        .filter(Boolean)
    : [];

  React.useEffect(() => {
    setAreChildrenExpanded(false);
    setAreMembersExpanded(false);
  }, [group]);

  React.useEffect(() => {
    if (!group) return;
    const parentRelations = getEntityRelations(group, RELATION_CHILD_OF, {
      kind: 'group',
    });
    if (!parentRelations?.length) {
      setParentEntities([]);
      return;
    }
    const fetchParents = async () => {
      setLoadingParents(true);
      try {
        const entities = await Promise.all(
          parentRelations.map(async parent => {
            const ref = stringifyEntityRef(parent);
            const entity = await catalogApi.getEntityByRef(ref);
            return entity as GroupEntity;
          }),
        );
        setParentEntities(entities.filter(Boolean));
      } catch {
        setParentEntities([]);
      } finally {
        setLoadingParents(false);
      }
    };
    fetchParents();
  }, [catalogApi, group]);

  React.useEffect(() => {
    if (!group) return;
    const childRelations = getEntityRelations(group, RELATION_PARENT_OF, {
      kind: 'group',
    });
    if (!childRelations?.length) {
      setChildEntities([]);
      return;
    }
    const fetchChildren = async () => {
      setLoadingChildren(true);
      try {
        const entities = await Promise.all(
          childRelations.map(async child => {
            const ref = stringifyEntityRef(child);
            const entity = await catalogApi.getEntityByRef(ref);
            return entity as GroupEntity;
          }),
        );
        setChildEntities(entities.filter(Boolean));
      } catch {
        setChildEntities([]);
      } finally {
        setLoadingChildren(false);
      }
    };
    fetchChildren();
  }, [catalogApi, group]);

  React.useEffect(() => {
    if (!group) return;
    const fetchMembers = async () => {
      setLoadingMembers(true);
      try {
        const { items } = await catalogApi.getEntities({
          filter: {
            kind: 'User',
            'relations.memberof': [stringifyEntityRef(group)],
          },
        });
        setMemberEntities(items);
      } catch {
        setMemberEntities([]);
      } finally {
        setLoadingMembers(false);
      }
    };
    fetchMembers();
  }, [catalogApi, group]);

  if (!group) {
    return (
      <Alert severity="error" data-testid="group-alert">
        Group not found
      </Alert>
    );
  }

  const slackCommunications: { channel: string; title: string }[] =
    (group.spec as any)?.communications?.filter(
      (c: any) => c.type === 'slack' && c.channel && c.title,
    ) ?? [];

  const slackItems: SideListItem[] = slackCommunications.map(comm => ({
    label: comm.channel,
    href: comm.title,
    icon: <SlackLogo />,
  }));

  const jiraItems: SideListItem[] = jiraProjects.map(projectKey => ({
    label: projectKey,
    href: `https://tomtom.atlassian.net/browse/${projectKey}`,
    icon: <JiraProjectsLogo />,
  }));

  const confluenceItems: SideListItem[] = confluenceLinks.map((url, idx) => ({
    label: `Wiki Link ${idx + 1}`,
    href: url,
    icon: <ConfluenceLogo />,
  }));

  const INITIAL_MEMBERS_COUNT = 2;
  const visibleMembers = memberEntities.slice(0, INITIAL_MEMBERS_COUNT);
  const collapsibleMembers = memberEntities.slice(INITIAL_MEMBERS_COUNT);

  return (
    <InfoCard
      variant={props.variant}
      className={classes.compactCard}
      title="Organization View"
    >
      <Box className={classes.contentWrapper}>
        <Divider style={{ marginTop: -18 }} />
        <Box className={classes.mainContent}>
          <Box className={classes.leftSection}>
            <Box className={classes.hierarchyContainer}>
              <Typography className={classes.parentLabel}>Manager</Typography>
              {group.metadata.annotations?.[
                'graph.microsoft.com/group-manager'
              ] && (
                <ManagerNode
                  managerName={
                    group.metadata.annotations[
                      'workday.com/supervisory-org-manager'
                    ]
                  }
                  managerEmail={
                    group.metadata.annotations[
                      'graph.microsoft.com/group-manager'
                    ]
                  }
                />
              )}

              <Box className={classes.membersSection}>
                <Box className={classes.membersSectionHeader}>
                  <Typography className={classes.parentLabel}>
                    Members ({memberEntities.length})
                  </Typography>
                </Box>
                {loadingMembers ? (
                  <Box className={classes.teamNode}>
                    <Progress />
                  </Box>
                ) : (
                  <Box>
                    <Grid container className={classes.membersGrid}>
                      {visibleMembers.map(member => (
                        <Grid
                          item
                          xs={6}
                          key={member.metadata.uid ?? member.metadata.name}
                          className={classes.memberGridItem}
                        >
                          <MemberNode member={member} />
                        </Grid>
                      ))}
                    </Grid>
                    {collapsibleMembers.length > 0 && (
                      <>
                        <Collapse in={areMembersExpanded}>
                          <Grid container>
                            {collapsibleMembers.map(member => (
                              <Grid
                                item
                                xs={6}
                                key={
                                  member.metadata.uid ?? member.metadata.name
                                }
                                className={classes.memberGridItem}
                              >
                                <MemberNode member={member} />
                              </Grid>
                            ))}
                          </Grid>
                        </Collapse>
                        <Box display="flex" justifyContent="center">
                          <Button
                            className={classes.showMoreButton}
                            onClick={() =>
                              setAreMembersExpanded(!areMembersExpanded)
                            }
                            startIcon={
                              areMembersExpanded ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )
                            }
                          >
                            {areMembersExpanded
                              ? 'Show Less'
                              : `Show ${collapsibleMembers.length} More`}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>

              {loadingParents && (
                <Box className={classes.teamNode}>
                  <Progress />
                </Box>
              )}
              {!loadingParents && parentEntities.length > 0 && (
                <>
                  <Typography className={classes.parentLabel}>
                    Team Hierarchy
                  </Typography>
                  {parentEntities.map(parent => (
                    <TeamNode
                      key={parent.metadata.name}
                      entity={parent}
                      isCurrent={false}
                      isParent
                    />
                  ))}
                </>
              )}
              <TeamNode entity={group} isCurrent />
              <Box>
                <Box
                  className={classes.hierarchyHeader}
                  onClick={() =>
                    childEntities.length > 0 &&
                    setAreChildrenExpanded(!areChildrenExpanded)
                  }
                >
                  <Typography className={classes.childrenLabel}>
                    Child Teams ({childEntities.length})
                  </Typography>
                  {childEntities.length > 0 && (
                    <IconButton size="small">
                      {areChildrenExpanded ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  )}
                </Box>
                <Collapse in={areChildrenExpanded}>
                  {loadingChildren ? (
                    <Box className={classes.teamNode}>
                      <Progress />
                    </Box>
                  ) : (
                    childEntities.map(child => (
                      <TeamNode key={child.metadata.name} entity={child} />
                    ))
                  )}
                </Collapse>
              </Box>
            </Box>
          </Box>
          <Box className={classes.rightSection}>
            <Box className={classes.sideListContainer}>
              <SideListSection
                icon={<JiraProjectsLogo />}
                title="Jira Projects"
                count={jiraItems.length}
                items={jiraItems}
                defaultExpanded={jiraItems.length > 0}
              />
              <SideListSection
                icon={<SlackLogo />}
                title="Slack Channels"
                count={slackItems.length}
                items={slackItems}
                defaultExpanded={slackItems.length > 0}
              />
              <SideListSection
                icon={<ConfluenceLogo />}
                title="Confluence"
                count={confluenceItems.length}
                items={confluenceItems}
                defaultExpanded={confluenceItems.length > 0}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </InfoCard>
  );
};
